import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import {
  twoFactorService,
  type TwoFactorEnrolment,
  type TwoFactorStatus,
} from '../../services/twofactor-service';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';

/**
 * Which part of the flow the user is in.
 *
 * Deliberately a single state rather than a pile of booleans: the recovery
 * codes screen must be reachable from two places (enrolment, regeneration)
 * and leavable from exactly one, and that is hard to keep right otherwise.
 */
type Stage = 'loading' | 'off' | 'enrolling' | 'codes' | 'on';

const rule = { borderTop: '1px solid var(--border-soft)' };

/**
 * Two-step sign-in settings.
 *
 * Follows the marketing-page house style rather than the older card-based
 * settings screens: hairline rules, sentence-case labels, tokens throughout so
 * it reads correctly in both themes, and the accent colour reserved for the
 * one primary action on screen.
 */
export const TwoFactorSetup: React.FC = () => {
  const [stage, setStage] = useState<Stage>('loading');
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [enrolment, setEnrolment] = useState<TwoFactorEnrolment | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  // Which management action the code box is currently for. Regenerating and
  // turning off both require a code, and mixing them up would be a nasty
  // surprise, so the intent is explicit.
  const [intent, setIntent] = useState<'none' | 'regenerate' | 'disable'>('none');

  const load = useCallback(async () => {
    try {
      const s = await twoFactorService.status();
      setStatus(s);
      setStage(s.enabled ? 'on' : 'off');
    } catch (e: any) {
      setError(e?.message || 'Could not load your two-step sign-in settings');
      setStage('off');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const reset = () => {
    setCode('');
    setError(null);
    setIntent('none');
    setShowSecret(false);
  };

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnrolment(await twoFactorService.beginEnrolment());
      setStage('enrolling');
    } catch (e: any) {
      setError(e?.message || 'Could not start setup');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await twoFactorService.confirmEnrolment(code);
      setCodes(result.recovery_codes);
      setEnrolment(null);
      reset();
      setStage('codes');
    } catch (e: any) {
      setError(e?.message || 'That code is not valid');
    } finally {
      setBusy(false);
    }
  };

  const runManagementAction = async () => {
    setBusy(true);
    setError(null);
    try {
      if (intent === 'regenerate') {
        const result = await twoFactorService.regenerateRecoveryCodes(code);
        setCodes(result.recovery_codes);
        reset();
        setStage('codes');
      } else {
        await twoFactorService.disable(code);
        reset();
        await load();
      }
    } catch (e: any) {
      setError(e?.message || 'That code is not valid');
    } finally {
      setBusy(false);
    }
  };

  const heading = (
    <>
      <Typography sx={{ fontWeight: 650, fontSize: '1.05rem', mb: 0.75 }}>
        Two-step sign-in
      </Typography>
      <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 560, mb: 3 }}>
        Require a code from your phone as well as your password. Without it, a
        leaked password is enough to reach every account balance in FolioWorth.
      </Typography>
    </>
  );

  const errorLine = error ? (
    <Typography role="alert" sx={{ color: 'var(--danger-text)', mb: 2 }}>
      {error}
    </Typography>
  ) : null;

  // ---- Recovery codes, shown once ----------------------------------------
  if (stage === 'codes') {
    return (
      <Box sx={{ ...rule, pt: 3 }}>
        <RecoveryCodesPanel
          codes={codes}
          onAcknowledge={() => { setCodes([]); void load(); }}
        />
      </Box>
    );
  }

  // ---- Enrolment ---------------------------------------------------------
  if (stage === 'enrolling' && enrolment) {
    return (
      <Box sx={{ ...rule, pt: 3 }}>
        {heading}
        {errorLine}

        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          1. Scan this with your authenticator app
        </Typography>
        <Box
          component="img"
          src={enrolment.qr_data_uri}
          alt="QR code for setting up two-step sign-in"
          sx={{
            width: 200, height: 200, display: 'block', mb: 1.5,
            // The QR is black-on-white by construction, so it needs a white
            // plate to stay scannable when the app is in dark mode.
            background: '#fff', padding: '10px', borderRadius: 'var(--radius-sm)',
          }}
        />

        <Box sx={{ mb: 3 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            sx={{
              background: 'none', border: 'none', p: 0, font: 'inherit',
              fontSize: '0.875rem', cursor: 'pointer', color: 'var(--text-faint)',
              borderBottom: '1px solid transparent',
              '&:hover': { color: 'var(--text)', borderBottomColor: 'var(--text)' },
            }}
          >
            {showSecret ? 'Hide the setup key' : 'Can’t scan? Enter a key instead'}
          </Box>
          {showSecret && (
            <Typography
              className="fw-num"
              sx={{ mt: 1, wordBreak: 'break-all', maxWidth: 420, fontSize: '0.9375rem' }}
            >
              {enrolment.secret}
            </Typography>
          )}
        </Box>

        <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
          2. Enter the six-digit code it shows
        </Typography>
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => { e.preventDefault(); void confirm(); }}
          sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}
        >
          <TextField
            size="small"
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', maxLength: 7, 'aria-label': 'Verification code' }}
            sx={{ width: 180 }}
          />
          <Button type="submit" variant="contained" disabled={busy || !code}>
            Turn on
          </Button>
          <Button
            variant="text"
            disabled={busy}
            onClick={() => { setEnrolment(null); reset(); setStage('off'); }}
          >
            Cancel
          </Button>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--text-faint)' }}>
          Nothing is switched on until this code checks out — so a mis-scanned
          QR cannot lock you out.
        </Typography>
      </Box>
    );
  }

  // ---- Enabled -----------------------------------------------------------
  if (stage === 'on' && status) {
    const low = status.recovery_codes_remaining <= 2;
    return (
      <Box sx={{ ...rule, pt: 3 }}>
        {heading}
        {errorLine}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, ...rule }}>
          <Typography variant="body2">Status</Typography>
          <Typography variant="body2" sx={{ color: 'var(--inflow-text)' }}>on</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, ...rule }}>
          <Typography variant="body2">Recovery codes left</Typography>
          <Typography
            variant="body2"
            className="fw-num"
            sx={{ color: low ? 'var(--warn-text)' : 'var(--text-secondary)' }}
          >
            {status.recovery_codes_remaining} of {status.recovery_codes_total}
          </Typography>
        </Box>

        {low && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--warn-text)' }}>
            Running low. Generate a new set before you are down to none.
          </Typography>
        )}

        {intent === 'none' ? (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => setIntent('regenerate')}>
              New recovery codes
            </Button>
            <Button variant="text" size="small" onClick={() => setIntent('disable')}>
              Turn off
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 1.5, maxWidth: 560 }}>
              {intent === 'regenerate'
                ? 'Enter a code to confirm. Every existing recovery code stops working.'
                : 'Enter a code to confirm. Being signed in is not enough to remove this.'}
            </Typography>
            <Box
              component="form"
              onSubmit={(e: React.FormEvent) => { e.preventDefault(); void runManagementAction(); }}
              sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
            >
              <TextField
                size="small"
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                autoComplete="one-time-code"
                inputProps={{ 'aria-label': 'Code' }}
                sx={{ width: 200 }}
              />
              <Button
                type="submit"
                variant={intent === 'regenerate' ? 'contained' : 'outlined'}
                disabled={busy || !code}
              >
                {intent === 'regenerate' ? 'Generate' : 'Turn off'}
              </Button>
              <Button variant="text" disabled={busy} onClick={reset}>
                Cancel
              </Button>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--text-faint)' }}>
              A recovery code works here too.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ---- Off, or still loading --------------------------------------------
  return (
    <Box sx={{ ...rule, pt: 3 }}>
      {heading}
      {errorLine}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, ...rule }}>
        <Typography variant="body2">Status</Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-faint)' }}>
          {stage === 'loading' ? '…' : 'off'}
        </Typography>
      </Box>
      <Button
        variant="contained"
        sx={{ mt: 3 }}
        disabled={busy || stage === 'loading'}
        onClick={begin}
      >
        Set up two-step sign-in
      </Button>
    </Box>
  );
};
